/**
 * IdentityFields — composable section for step 1 (and reusable in /perfil).
 */

import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { PhotoUpload } from "./PhotoUpload";

export const identityFieldsSchema = z.object({
  honorific_prefix: z.enum(["", "Dr.", "Dra."]).default(""),
  display_name: z.string().trim().min(2, "Informe seu nome"),
  gender: z.enum(["M", "F"], { required_error: "Selecione" }),
  age: z.coerce.number().int().min(16, "Idade mínima 16").max(110, "Idade inválida").nullable(),
  photo_url: z.string().url().nullable().optional(),
});

export type IdentityFieldsValues = z.infer<typeof identityFieldsSchema>;

const LABELS: Record<keyof IdentityFieldsValues, string> = {
  honorific_prefix: "tratamento",
  display_name: "nome",
  gender: "gênero",
  age: "idade",
  photo_url: "foto",
};

export interface IdentityFieldsProps {
  defaultValues?: Partial<IdentityFieldsValues>;
  onChange?: (patch: Partial<IdentityFieldsValues>) => void;
  onValidityChange?: (valid: boolean) => void;
  onMissingChange?: (labels: string[]) => void;
  registerForm?: (form: ReturnType<typeof useForm<IdentityFieldsValues>>) => void;
  validateOnMount?: boolean;
}

export function IdentityFields({
  defaultValues,
  onChange,
  onValidityChange,
  onMissingChange,
  registerForm,
  validateOnMount = false,
}: IdentityFieldsProps) {
  const form = useForm<IdentityFieldsValues>({
    resolver: zodResolver(identityFieldsSchema),
    mode: "onChange",
    defaultValues: {
      honorific_prefix: defaultValues?.honorific_prefix ?? "",
      display_name: defaultValues?.display_name ?? "",
      gender: defaultValues?.gender,
      age: defaultValues?.age,
      photo_url: defaultValues?.photo_url ?? null,
    },
  });

  useEffect(() => {
    registerForm?.(form);
  }, [form, registerForm]);

  useEffect(() => {
    onValidityChange?.(form.formState.isValid);
  }, [form.formState.isValid, onValidityChange]);

  useEffect(() => {
    if (validateOnMount) void form.trigger();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!onMissingChange) return;
    const errs = form.formState.errors;
    const labels = (Object.keys(errs) as Array<keyof IdentityFieldsValues>)
      .map((k) => LABELS[k])
      .filter(Boolean);
    onMissingChange(labels);
  }, [form.formState.errors, onMissingChange]);

  const { register, control, watch, formState } = form;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="ob-honorific">Honorífico (opcional)</Label>
        <Controller
          name="honorific_prefix"
          control={control}
          render={({ field }) => (
            <Select
              value={(field.value ?? "") || "__none__"}
              onValueChange={(value) => {
                const next = (value === "__none__" ? "" : value) as "" | "Dr." | "Dra.";
                field.onChange(next);
                onChange?.({ honorific_prefix: next });
              }}
            >
              <SelectTrigger id="ob-honorific">
                <SelectValue placeholder="Sem honorífico" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sem honorífico</SelectItem>
                <SelectItem value="Dr.">Dr.</SelectItem>
                <SelectItem value="Dra.">Dra.</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="ob-name">Como você gostaria de ser chamado?</Label>
        <Input
          id="ob-name"
          autoComplete="name"
          placeholder="Nome Sobrenome"
          {...register("display_name", {
            onChange: (e) => onChange?.({ display_name: e.target.value }),
          })}
        />
        {formState.errors.display_name ? (
          <p className="text-xs text-destructive">{formState.errors.display_name.message}</p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label>Gênero</Label>
        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <RadioGroup
              value={field.value ?? ""}
              onValueChange={(v) => {
                const next = v as "M" | "F";
                field.onChange(next);
                onChange?.({ gender: next });
              }}
              className="grid grid-cols-2 gap-2"
            >
              {(["M", "F"] as const).map((g) => (
                <Label
                  key={g}
                  htmlFor={`gender-${g}`}
                  className={cn(
                    "flex cursor-pointer items-center justify-center rounded-xl border border-border/60 p-3 text-sm transition hover:border-primary/60",
                    watch("gender") === g && "border-primary bg-primary/5",
                  )}
                >
                  <RadioGroupItem id={`gender-${g}`} value={g} className="sr-only" />
                  {g === "M" ? "Masculino" : "Feminino"}
                </Label>
              ))}
            </RadioGroup>
          )}
        />
        {formState.errors.gender ? (
          <p className="text-xs text-destructive">{formState.errors.gender.message}</p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="ob-age">Idade</Label>
        <Input
          id="ob-age"
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          min={16}
          max={110}
          {...register("age", {
            valueAsNumber: true,
            onChange: (e) => {
              const raw = e.target.value;
              if (!raw) return;
              const n = Number(raw);
              if (!Number.isNaN(n)) onChange?.({ age: n });
            },
          })}
        />
        {formState.errors.age ? (
          <p className="text-xs text-destructive">{formState.errors.age.message}</p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label>Foto (opcional)</Label>
        <Controller
          name="photo_url"
          control={control}
          render={({ field }) => (
            <PhotoUpload
              value={field.value ?? null}
              onChange={(url) => {
                field.onChange(url);
                onChange?.({ photo_url: url });
              }}
            />
          )}
        />
      </div>
    </div>
  );
}
